import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Message from '../../../shared/message/Message.js';
import GenericTab from '../../../shared/generictab/GenericTab.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {regrasDoPrograma} from '../../components/processo/processoSelecao.js';
import TabInscricaoPeriodo from './inscricao/TabInscricaoPeriodo.jsx';
import TabInscricaoProcessosVestibular from './inscricao/TabInscricaoProcessosVestibular.jsx';
import TabInscricaoCursos from './inscricao/TabInscricaoCursos.jsx';
import TabInscricaoEnem from './inscricao/TabInscricaoEnem.jsx';
import TabInscricaoOpcoes from './inscricao/TabInscricaoOpcoes.jsx';

// O GenericTab exige no mínimo 3 abas; com menos (PROUNI e FIES), o conteúdo vai direto.
const MINIMO_SUBABAS = 3;

// Campos de cada subaba, para marcar a que tem erro e abri-la ao salvar.
const CAMPOS_POR_SUBABA = Object.freeze({
    periodo: ['dataInicial', 'dataFinal', 'dataFinalAtualizacaoResultado', 'termoCiencia', 'informacaoAdicional'],
    processosVestibular: ['processosVestibular'],
    cursos: ['vagas'],
    enems: ['enems'],
    opcoes: ['disponivelParaFuncionario', 'flagRevalidacao', 'flagSomenteInscricaoComCargas', 'flagProtocoloEntrega']
});

const useStyles = makeStyles(theme => ({
    avisos: {
        marginBottom: theme.spacing(2)
    }
}));

/*
 * Aba "Inscrição": avisos do programa e subabas horizontais — Período e termos,
 * Processos seletivos (Ingressante e Colégio Técnico), Cursos e Enem
 * (Ingressante), cada uma só nos programas em que o _form.xhtml exibe o bloco —
 * e Opções, que vale para todos.
 */
export default function TabInscricao(props) {
    const {control, errors, watch, setValue, cursos = [], campi = [], turnos = [], enems = [], processosVestibularApoio = []} = props;
    const classes = useStyles();
    const confirmation = useConfirmationDialog();
    const subabasRef = useRef();

    const idPrograma = watch('idPrograma');
    const regras = regrasDoPrograma(idPrograma);
    const processosVestibular = watch('processosVestibular') || [];
    const vagas = watch('vagas') || [];
    const enemsProcesso = watch('enems') || [];

    const alterarLista = (nome, lista) => setValue(nome, lista, {shouldDirty: true, shouldValidate: !!errors[nome]});

    const remover = (nome, lista, posicao, pergunta) => {
        confirmation(pergunta, () => alterarLista(nome, lista.filter((item, atual) => atual !== posicao)));
    };

    const listaProps = {errors, onAlterarLista: alterarLista, onRemover: remover};

    const subabas = [
        {
            id: 'periodo',
            label: 'Período e termos',
            visible: regras.exibirInscricao,
            content: <TabInscricaoPeriodo control={control} errors={errors}/>
        },
        {
            id: 'processosVestibular',
            label: regras.processoVestibularUnico ? 'Processo seletivo' : 'Processos seletivos',
            visible: regras.exibirInscricao && regras.exibirProcessosVestibular,
            content: (
                <TabInscricaoProcessosVestibular
                    {...listaProps}
                    idPrograma={idPrograma}
                    unico={regras.processoVestibularUnico}
                    processosVestibular={processosVestibular}
                    processosVestibularApoio={processosVestibularApoio}/>
            )
        },
        {
            id: 'cursos',
            label: 'Cursos',
            visible: regras.exibirInscricao,
            content: <TabInscricaoCursos {...listaProps} vagas={vagas} campi={campi} cursos={cursos} turnos={turnos}/>
        },
        {
            id: 'enems',
            label: 'Enem',
            visible: regras.exibirInscricao && regras.exibirEnems,
            content: <TabInscricaoEnem {...listaProps} enemsProcesso={enemsProcesso} enems={enems}/>
        },
        {
            id: 'opcoes',
            label: 'Opções',
            content: <TabInscricaoOpcoes control={control} errors={errors}/>
        }
    ]
        .map(subaba => ({...subaba, hasError: CAMPOS_POR_SUBABA[subaba.id].some(campo => !!errors[campo])}))
        .filter(subaba => subaba.visible !== false);

    // Ao surgir erro (ex.: ao salvar), abre a primeira subaba com erro.
    const chaveErros = subabas.filter(subaba => subaba.hasError).map(subaba => subaba.id).join(',');
    useEffect(() => {
        const indice = subabas.findIndex(subaba => subaba.hasError);
        if (indice >= 0 && subabasRef.current) {
            subabasRef.current.setTabIndex(indice);
        }
    }, [chaveErros]);

    return (
        <>
            {(!regras.programaSelecionado || regras.avisoInscricaoGoverno) && (
                <Grid container spacing={3} className={classes.avisos}>
                    {!regras.programaSelecionado && (
                        <Grid item xs={12}>
                            <Message type='warning' variant='standard'>Programa ofertado não foi selecionado.</Message>
                        </Grid>
                    )}
                    {regras.avisoInscricaoGoverno && (
                        <Grid item xs={12}>
                            <Message type='info' variant='standard'>
                                O programa ofertado selecionado não permite a configuração de inscrição, uma vez que estas ocorrem no site do governo.
                            </Message>
                        </Grid>
                    )}
                </Grid>
            )}
            {subabas.length >= MINIMO_SUBABAS
                ? <GenericTab ref={subabasRef} content={subabas}/>
                : subabas.map(subaba => <React.Fragment key={subaba.id}>{subaba.content}</React.Fragment>)}
        </>
    );
}

TabInscricao.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    /* Cursos do apoio para o programa e os processos vestibulares atuais. */
    cursos: PropTypes.array,
    campi: PropTypes.array,
    turnos: PropTypes.array,
    /* ENEMs do apoio: [{id, ano}]. */
    enems: PropTypes.array,
    /* Processos vestibulares do apoio: [{id, descricao, tipo}]. */
    processosVestibularApoio: PropTypes.array
};
